import { ChangeDetectorRef, Component, Input } from '@angular/core';
import { CDFTService } from 'app/modules/cdft/services/cdft.service';
import { forkJoin } from 'rxjs';
import { WellStringStatusState } from 'app/modules/cdft/state/well-string-status.state';
import { StringEquipmentState } from 'app/modules/cdft/state/string-equipment.state';
import {
    FormArray,
    FormBuilder,
    FormControl,
    FormGroup,
    Validators,
} from '@angular/forms';
import { FileInputValidators, FileInputValue } from '@ngx-dropzone/cdk';
import { CDFTState } from 'app/modules/cdft/state/cdft.state';
import { MatDialog } from '@angular/material/dialog';
import { ImageViewerDialogComponent } from 'app/core/shared/component/image-viewer-dialog/image-viewer-dialog.component';
import { ImageViewer } from 'app/core/shared/component/image-viewer-dialog/image-viewer-dialog.model';
import { DateTime } from 'luxon';
import module from 'app/modules/cdft/cdft.module';
import { HeaderAttachment } from '../../model';

@Component({
    selector: 'header',
    templateUrl: './header.component.html',
    standalone: true,
    styleUrls: ['./header.component.scss'],
    imports: module,
})
export class HeaderComponent {
    @Input() isViewOnly: boolean;
    formGroup: FormGroup;
    headerForm: FormGroup;

    attachementFormat: string =
        'image/*,application/pdf,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword';

    observable$ = {
        cdftState: null,
        attachment: null,
    };

    constructor(
        private service: CDFTService,
        private stringEquipmentState: StringEquipmentState,
        private wellStringStatusState: WellStringStatusState,
        private formBuilder: FormBuilder,
        private state: CDFTState,
        private dialog: MatDialog,
        private changeDetectorRef: ChangeDetectorRef
    ) {}

    ngOnInit() {
        this.formGroup = this.onFormGroup();

        this.state.setFormGroup('cdftHeader', this.formGroup);
        this.headerForm = this.formBuilder.group({
            platformName: new FormControl({ value: null, disabled: true }),
            wellName: new FormControl({ value: null, disabled: true }),
            wellType: new FormControl({ value: null, disabled: true }),
            flowCategory: new FormControl({ value: null, disabled: true }),
            attachments: new FormControl<FileInputValue>(
                { value: null, disabled: this.isViewOnly },
                [FileInputValidators.accept(this.attachementFormat)]
            ),
            stringStatus: this.formBuilder.group({
                activePTN: new FormControl({ value: null, disabled: true }),
                activePTL: new FormControl({ value: null, disabled: true }),
                intermittent: new FormControl({ value: null, disabled: true }),
                gasInjector: new FormControl({ value: null, disabled: true }),
                waterInjector: new FormControl({ value: null, disabled: true }),
                idleProducer: new FormControl({ value: null, disabled: true }),
                idleInjector: new FormControl({ value: null, disabled: true }),
            }),
        });
        this.addAttachment();
        this.onPopulate();

        this.toggleField(
            'isImposedPressure',
            'imposedPressure',
            true,
            'cdftHeader.imposedPressure'
        );
        this.toggleField(
            'treeCapConn',
            'treecapConnRemarks',
            'others',
            'cdftHeader.treecapConnRemarks'
        );
        this.toggleField(
            'treeCapCond',
            'treecapCondRemarks',
            'others',
            'cdftHeader.treecapCondRemarks'
        );
    }

    ngOnDestroy() {
        for (const key in this.observable$) {
            this.observable$[key]?.unsubscribe();
        }
    }

    addAttachment() {
        this.observable$.attachment = this.headerForm
            .get('attachments')
            .valueChanges.subscribe((attachments) => {
                attachments.forEach((attachment) => {
                    const cdftAttachments = this.formGroup.get(
                        'cdftAttachments'
                    ) as FormArray;

                    const cdftAttachment: any = new FormControl({
                        cdftAttachmentId: new FormControl({
                            value: null,
                            disabled: false,
                        }),
                        file: new FormControl<FileInputValue>(
                            { value: attachment, disabled: true },
                            [FileInputValidators.accept(this.attachementFormat)]
                        ),
                        url: new FormControl({
                            value: null,
                            disabled: false,
                        }),
                        name: new FormControl({
                            value: attachment.name,
                            disabled: false,
                        }),
                    });

                    cdftAttachments.push(cdftAttachment);
                });

                const cdftAttachments =
                    this.formGroup.get('cdftAttachments').value;

                let stateCdftAttachments: HeaderAttachment[] = [];
                cdftAttachments.forEach((attachment) => {
                    const stateCdftAttachment: HeaderAttachment = {
                        cdftAttachmentId: null,
                        file: attachment.file.getRawValue(),
                        name: attachment.name.getRawValue(),
                        url: null,
                    };

                    stateCdftAttachments.push(stateCdftAttachment);
                });

                this.state.cdftHeader.cdftAttachments = stateCdftAttachments;
            });
    }

    onPopulate() {
        this.observable$.cdftState = this.state.subscribe((cdft: CDFTState) => {
            let wellStringId = '';
            if (!cdft.cdftHeader) return;

            if (cdft.cdftHeader.shutInTimeHrs) {
                const shutInTimeHrs = DateTime.fromISO(
                    cdft.cdftHeader.shutInTimeHrs
                );
                cdft.cdftHeader.shutInTimeHrs = shutInTimeHrs.toFormat('HH:mm');
            }
            if (cdft.cdftHeader.date) {
                const date = DateTime.fromISO(cdft.cdftHeader.date);
                cdft.cdftHeader.date = date as any;
            }
            this.formGroup.patchValue(cdft.cdftHeader);
            this.clearFormArray(
                this.formGroup.get('cdftAttachments') as FormArray
            );
            const cdftAttachments = this.formGroup.get(
                'cdftAttachments'
            ) as FormArray;

            cdft.cdftHeader.cdftAttachments.forEach((attachment) => {
                const cdftAttachment: any = new FormControl({
                    cdftAttachmentId: new FormControl({
                        value: attachment.cdftAttachmentId,
                        disabled: false,
                    }),
                    file: new FormControl<FileInputValue>(
                        { value: null, disabled: true },
                        [FileInputValidators.accept(this.attachementFormat)]
                    ),
                    url: new FormControl({
                        value: attachment.url,
                        disabled: false,
                    }),
                    name: new FormControl({
                        value: attachment.name ?? attachment.cdftAttachmentId,
                        disabled: false,
                    }),
                });
                cdftAttachments.push(cdftAttachment);
            });
            wellStringId = cdft.cdftHeader.wellStringId;

            this.service
                .getWellString(wellStringId)
                .subscribe((response: any) => {
                    this.onSelectedString({
                        id: response.data.id,
                    });
                });
        });
    }

    clearFormArray = (formArray: FormArray) => {
        while (formArray.length > 0) {
            formArray.removeAt(0);
        }
    };

    remove(cdftAttachment: any, index: number) {
        const attachments = this.formGroup.get('cdftAttachments') as FormArray;
        if (!cdftAttachment.cdftAttachmentId.value)
            return attachments.removeAt(index);
        this.service
            .deleteCdftAttachment(cdftAttachment.cdftAttachmentId.value)
            .subscribe((response) => {
                attachments.removeAt(index);
            });
    }

    async view(e, index: number) {
        e.stopPropagation();
        e.preventDefault();

        const imgFileType = /\.(png|jpe?g|webp|gif|bmp|svg)$/i;
        const images = [];
        const blobImages = [];

        const attachment = this.formGroup.get('cdftAttachments').getRawValue();
        const currentAttachement = attachment[index];
        const isImage = imgFileType.test(currentAttachement.name.value);

        // handle for file other than image
        if (!isImage) {
            if (!currentAttachement?.url?.value) {
                if (currentAttachement?.file?.value) {
                    const url = URL.createObjectURL(
                        currentAttachement?.file.value
                    );
                    this.downloadFile(url, currentAttachement.name.value);
                }

                return;
            }

            const attachmentId = currentAttachement?.cdftAttachmentId?.value;
            if (attachmentId) {
                this.service
                    .getAttachmentImageUrl(attachmentId)
                    .subscribe((value) => {
                        this.downloadFile(value, currentAttachement.name.value);
                    });
            }
            return;
        }

        // handle image file
        for (const key in attachment) {
            const row = attachment[key];
            const value: any = {};
            for (const key in row) {
                value[key] = row[key].value;
            }

            // Skip if not an image (based on file type or extension)
            const fileName = value.file?.name || value.name || value.url || '';
            const isImage = imgFileType.test(fileName);

            if (!isImage) continue;

            if (!value.url) {
                value.url = await this.extractImg(value.file);
            }

            images.push(value.url);

            const id = new URL(value.url).searchParams.get('id');

            if (id) {
                this.service.getAttachmentImageUrl(id).subscribe((value) => {
                    blobImages.push(value);
                });
            }
        }

        // not count file type other than image
        const newCurrentIndex = images.indexOf(currentAttachement.url.value);

        // return images;
        const data: ImageViewer = {
            current: newCurrentIndex,
            images,
            blobImages,
        };

        this.dialog.open(ImageViewerDialogComponent, {
            data,
            width: '70%',
            panelClass: 'transparent-dialog',
        });
    }

    private downloadFile(url, filename: string) {
        const link = document.createElement('a');
        link.href = url;
        link.download = filename ?? 'CDFT Attachement File.pdf'; // or .xlsx, .jpg, etc.
        link.click();
    }

    extractImg(file) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = function (e) {
                resolve(e.target.result);
            };
            reader.readAsDataURL(file);
        });
    }

    onFormGroup() {
        return this.formBuilder.group({
            id: new FormControl({ value: null, disabled: true }),
            cdftRecordId: new FormControl({ value: null, disabled: true }),

            // TEMP START
            platformName: new FormControl({ value: null, disabled: true }),
            wellName: new FormControl({ value: null, disabled: true }),
            wellType: new FormControl({ value: null, disabled: true }),
            wellFlowCategory: new FormControl({ value: null, disabled: true }),
            // TEMP END
            wellStringId: new FormControl({ value: null, disabled: true }),
            wellStatus: new FormControl({ value: null, disabled: true }),

            productionRateBblDay: new FormControl({
                value: null,
                disabled: this.isViewOnly,
            }),
            injectionRateMmscfD: new FormControl({
                value: null,
                disabled: this.isViewOnly,
            }),
            shutInTimeHrs: new FormControl({
                value: null,
                disabled: this.isViewOnly,
            }),
            tester: new FormControl({
                value: null,
                disabled: this.isViewOnly,
            }),
            date: new FormControl(
                {
                    value: null,
                    disabled: this.isViewOnly,
                },
                this.isViewOnly ? [] : [Validators.required]
            ),

            xmtRating: new FormControl({ value: null, disabled: true }),
            xmtSize: new FormControl({ value: null, disabled: true }),
            xmtOem: new FormControl({ value: null, disabled: true }),
            isGasLiftedWell: new FormControl({
                value: null,
                disabled: true,
            }),
            gasLiftedHeader: new FormControl({
                value: null,
                disabled: this.isViewOnly,
            }),

            isImposedPressure: new FormControl({
                value: false,
                disabled: this.isViewOnly,
            }),
            imposedPressure: new FormControl({ value: null, disabled: true }),

            // Annulus
            fthp: new FormControl({ value: null, disabled: this.isViewOnly }),
            sithp: new FormControl({ value: null, disabled: this.isViewOnly }),
            pcp: new FormControl({ value: null, disabled: this.isViewOnly }),
            icp: new FormControl({ value: null, disabled: this.isViewOnly }),
            scp: new FormControl({ value: null, disabled: this.isViewOnly }),
            ccp: new FormControl({ value: null, disabled: this.isViewOnly }),
            header: new FormControl({ value: null, disabled: this.isViewOnly }),

            stringStatus: new FormControl({
                value: null,
                disabled: this.isViewOnly,
            }),
            stringIndicator: new FormControl({
                value: null,
                disabled: this.isViewOnly,
            }),

            treeCapConn: new FormControl({
                value: null,
                disabled: true,
            }),
            treeCapConnRemarks: new FormControl({
                value: null,
                disabled: true,
            }),
            size: new FormControl({ value: null, disabled: true }),
            thread: new FormControl({ value: null, disabled: true }),
            treeCapCond: new FormControl({
                value: null,
                disabled: this.isViewOnly,
            }),
            treecapCondRemarks: new FormControl({
                value: null,
                disabled: this.isViewOnly,
            }), // tree cap condition if others

            voidArea: new FormControl({
                value: null,
                disabled: this.isViewOnly,
            }),
            voidAreaAfter: new FormControl({
                value: null,
                disabled: this.isViewOnly,
            }),

            isPressureBuildUpTest: new FormControl({
                value: null,
                disabled: this.isViewOnly,
            }),
            pressureBuildUpTest: new FormControl({
                value: null,
                disabled: this.isViewOnly,
            }),
            xmtPhysCond: new FormControl({
                value: null,
                disabled: this.isViewOnly,
            }),
            wellheadPhysCond: new FormControl({
                value: null,
                disabled: this.isViewOnly,
            }),
            cdftAttachments: this.formBuilder.array([]),
        });
    }

    onSelectedString(string: any) {
        forkJoin({
            stringEquipment: this.service.getStringEquipment(string.id),
            wellProduction: this.service.getWellProductionCategory(string.id),
        }).subscribe(() => {
            this.wellStringStatusState.nullToString();
            this.populateForm();
        });
    }

    populateForm() {
        this.stringStatusComponent();
        this.formGroup.patchValue({
            ...this.wellStringStatusState.toHeaderForm(),
            ...this.wellStringStatusState.toHeaderFormApi(),
            ...this.stringEquipmentState.toHeaderForm(),
            isGasLiftedWell:
                this.headerForm.controls.stringStatus.get('activePTL').value !=
                ''
                    ? 'true'
                    : 'false',
        });
    }

    stringStatusComponent() {
        const type = this.stringStatusCondition();

        const data = {};

        for (const key in type) {
            data[key] = type[key]();
        }
        this.headerForm.controls.stringStatus.patchValue(data);
    }

    stringStatusCondition() {
        return {
            activePTN: () => {
                if (
                    this.wellStringStatusState.wellStringType
                        .toLocaleLowerCase()
                        .includes('injector')
                )
                    return '';
                if (
                    this.wellStringStatusState.prodStatus
                        .toLocaleLowerCase()
                        .includes('idle')
                )
                    return '';
                if (
                    this.wellStringStatusState.wellFlowCategory
                        .toLocaleLowerCase()
                        .includes('artificially')
                )
                    return '';
                if (this.wellStringStatusState.wellFlowCategory == '')
                    return '';
                return this.wellStringStatusState.stringIndicator;
            },
            activePTL: () => {
                if (
                    this.wellStringStatusState.wellStringType
                        .toLocaleLowerCase()
                        .includes('injector')
                )
                    return '';
                if (
                    this.wellStringStatusState.prodStatus
                        .toLocaleLowerCase()
                        .includes('idle')
                )
                    return '';
                if (
                    this.wellStringStatusState.wellFlowCategory
                        .toLocaleLowerCase()
                        .includes('natural')
                )
                    return '';
                if (this.wellStringStatusState.wellFlowCategory == '')
                    return '';
                return this.wellStringStatusState.stringIndicator;
            },
            intermittent: () => '',
            gasInjector: () => {
                if (
                    !this.wellStringStatusState.wellStringType
                        .toLocaleLowerCase()
                        .includes('gas injector')
                )
                    return '';
                if (
                    this.wellStringStatusState.prodStatus
                        .toLocaleLowerCase()
                        .includes('idle')
                )
                    return '';
                return this.wellStringStatusState.stringIndicator;
            },
            waterInjector: () => {
                if (
                    !this.wellStringStatusState.wellStringType
                        .toLocaleLowerCase()
                        .includes('water injector')
                )
                    return '';
                if (
                    this.wellStringStatusState.prodStatus
                        .toLocaleLowerCase()
                        .includes('idle')
                )
                    return '';
                return this.wellStringStatusState.stringIndicator;
            },
            idleProducer: () => {
                if (
                    this.wellStringStatusState.wellStringType
                        .toLocaleLowerCase()
                        .includes('injector')
                )
                    return '';
                if (
                    !this.wellStringStatusState.prodStatus
                        .toLocaleLowerCase()
                        .includes('idle')
                )
                    return '';
                return this.wellStringStatusState.stringIndicator;
            },
            idleInjector: () => {
                if (
                    this.wellStringStatusState.wellStringType
                        .toLocaleLowerCase()
                        .includes('producer')
                )
                    return '';
                if (
                    !this.wellStringStatusState.prodStatus
                        .toLocaleLowerCase()
                        .includes('idle')
                )
                    return '';
                return this.wellStringStatusState.stringIndicator;
            },
        };
    }

    formatDecimal(controlName: string): void {
        const ctrl = this.formGroup.get(controlName);
        if (!ctrl) return;

        let value = parseFloat(ctrl.value);
        if (!isNaN(value)) {
            ctrl.setValue(value.toFixed(2), { emitEvent: false });
        } else {
            ctrl.setValue(null, { emitEvent: false });
        }
    }

    private toggleField(
        trigger: string,
        target: string,
        triggerValue: any,
        statePath?: string
    ): void {
        this.formGroup.get(trigger)?.valueChanges.subscribe((value) => {
            const targetControl = this.formGroup.get(target);
            const isMatch =
                typeof triggerValue === 'string'
                    ? value?.toLowerCase() === triggerValue.toLowerCase()
                    : !!value === !!triggerValue;

            if (isMatch) {
                targetControl?.enable();
            } else {
                // Reset both form and state
                targetControl?.setValue('');
                targetControl?.disable();

                if (statePath) {
                    const [parentKey, childKey] = statePath.split('.');
                    if (this.state[parentKey])
                        this.state[parentKey][childKey] = null;
                }
            }

            this.changeDetectorRef.detectChanges();
        });
    }
}
